import { Banner, Spinner, Tabs } from "@shopify/polaris";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import Dashboard from "./dashboard";
import ErrorLog from "./errorLog";
import ShopLinks from "./shopLinks";
import AdminAccounts from "./adminAccounts";
import StorePerformance from "./storePerformance";
import { admin_context } from "../api";

const allPermissions = [
  "shops.view", "shops.manage", "performance.view", "performance.export",
  "logs.view", "logs.delete", "accounts.manage",
];

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const [context, setContext] = useState(null);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    let active = true;
    admin_context({}).then((response) => {
      if (active && response.status === 200) setContext(response.data);
      else if (active) setLoadError(true);
    }).catch(() => active && setLoadError(true));
    return () => { active = false; };
  }, []);

  const permissions = useMemo(() => context?.permissions || [], [context?.permissions]);
  const isOwner = Boolean(context?.is_owner);
  const can = useCallback((permission) => isOwner || permissions.includes(permission), [isOwner, permissions]);
  const tabs = useMemo(() => [
    { id: "Dashboard", content: "Shop settings", allowed: can("shops.view") },
    { id: "Performance", content: "Store performance", allowed: can("performance.view") },
    { id: "ErrorLog", content: "Error logs", allowed: can("logs.view") },
    { id: "Accounts", content: "Accounts & permissions", allowed: can("accounts.manage") },
    { id: "ShopLinks", content: "A/B shop links", allowed: isOwner },
  ].filter((tab) => tab.allowed), [can, isOwner]);

  const activeTab = tabs.find((tab) => `/${tab.id}` === location.pathname);
  useEffect(() => {
    if (!context) return;
    if (!activeTab) navigate(tabs.length ? `/${tabs[0].id}` : "/NoAccess", { replace: true });
  }, [activeTab, context, navigate, tabs]);

  if (loadError) return <Banner tone="critical" title="Unable to load administrator permissions. Please sign in again." />;
  if (!context) return <Spinner accessibilityLabel="Loading administrator permissions" />;
  if (!tabs.length) return <Banner title="No super-admin features are authorized for this account." />;

  return (
    <Tabs tabs={tabs} selected={Math.max(0, tabs.findIndex((tab) => tab.id === activeTab?.id))}
      onSelect={(index) => navigate(`/${tabs[index].id}`)}>
      <Routes>
        <Route path="/" element={<Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Dashboard" element={can("shops.view") ? <Dashboard canManage={can("shops.manage")} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Performance" element={can("performance.view") ? <StorePerformance /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/ErrorLog" element={can("logs.view") ? <ErrorLog canDelete={can("logs.delete")} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/Accounts" element={can("accounts.manage") ? <AdminAccounts context={context} permissions={allPermissions} /> : <Navigate to={`/${tabs[0].id}`} replace />} />
        <Route path="/ShopLinks" element={isOwner ? <ShopLinks /> : <Navigate to={`/${tabs[0].id}`} replace />} />
      </Routes>
    </Tabs>
  );
}
